// ─────────────────────────────────────────────
//  OR Board B — needs you first.
//
//  Inverts the other two. The board is reference; the thing the anesthesiologist
//  actually needs is a short RANKED list of what requires them now, with the
//  action attached. You should not have to scan twenty cards to find the three
//  things that need you.
//
//  Ranking lives in `domain/boardWorklist.js` and is tiered by consequence, not
//  by time: a nine-minute uncovered room outranks a forty-one-minute break wait.
//  Time only breaks ties inside a tier.
//
//  The calm state matters more than the busy one, because it is the state you
//  see most. It names what the board is watching on your behalf, so an empty
//  list reads as SUPERVISED rather than as a failed fetch — otherwise a working
//  panel with nothing to report and a broken one look identical.
// ─────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { logout } from "../firebase/auth";
import { useDayKey } from "../hooks/useDayKey";
import { useLiveConnection } from "../hooks/useLiveConnection";
import { useDeviceFacility } from "../hooks/useDeviceFacility";
import { useFacilities } from "../hooks/useFacilities";
import { useControlPanelData } from "../hooks/useControlPanelData";
import {
  assignDestinations, targetCaseFor, assignmentChangedSince,
  writesAllowed, WRITE_BLOCKED_COPY, stagedReliefRows,
  currentCrnaId, currentCrnaName,
} from "../domain/panelAssign";
import { nextBoundary, laneCoverage } from "../domain/controlPanel";
import { buildWorklist, watchingSummary, TIER_LABEL } from "../domain/boardWorklist";
import { placeProvider } from "../features/control-panel/placeProvider";
import { setRosterMemberDeparted, updateRoomStatus } from "../firebase/firestore";
import { confirmRelief } from "../features/relief/confirmRelief";
import { roomForOrRoom } from "../domain/gridTemplate";
import { groupRoomsByLocation } from "../domain/locations";
import { facilityLabel } from "../domain/facilities";
import { alertAgeLabel } from "../domain/alerts";
import NavMenu from "../components/NavMenu";
import SignOutButton from "../components/SignOutButton";
import BoardSwitcher from "../components/controlPanel/BoardSwitcher";
import PanelRoomCard from "../components/controlPanel/PanelRoomCard";
import PanelSheet from "../components/controlPanel/PanelSheet";
import FacilityPicker from "../components/controlPanel/FacilityPicker";
import BenchSections from "../components/controlPanel/BenchSections";
import styles, { sectionTint, C, MONO } from "../components/controlPanel/panelCard.styles";

const NOW_TICK_MS = 30_000;

function useNowMinutes() {
  const [nowMin, setNowMin] = useState(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  });
  useEffect(() => {
    const id = setInterval(() => {
      const d = new Date();
      setNowMin(d.getHours() * 60 + d.getMinutes());
    }, NOW_TICK_MS);
    return () => clearInterval(id);
  }, []);
  return nowMin;
}

const TIER_LOOK = {
  alarm: { bg: C.alarmBg, line: C.alarmLine, fg: C.alarm },
  act: { bg: C.warnBg, line: C.warnLine, fg: C.warn },
  plan: { bg: "#eef2f9", line: C.line, fg: C.muted },
};

export default function BoardBPage() {
  const { user, isAdmin, isAIC, isDoctor } = useAuth();
  const nowMin = useNowMinutes();
  const dateKey = useDayKey();
  const facilities = useFacilities();

  const device = useDeviceFacility({
    facilities,
    accountFacilityId: user?.activeFacilityId || null,
    allowedFacilityIds: user?.facilityIds,
    isAdmin,
  });
  const facilityId = device.facilityId;

  const breakWindows = useMemo(
    () => (facilities || []).find((f) => f && f.id === facilityId)?.breakWindows || null,
    [facilities, facilityId]
  );

  const {
    model, lanes, breakRowByRoomId, alarms, railMetrics, loading,
    assignments, rooms, available, locations, occupiedRooms,
    departedMembers, travelingMembers, breakWaitingRows, alertRows,
  } = useControlPanelData(facilityId, dateKey, { nowMin, breakWindows });

  const connection = useLiveConnection(facilityId);
  const isWallDisplay = user?.role === "boardwall";
  const canAct = Boolean(!isWallDisplay && (isAdmin || isAIC || isDoctor));

  const [sheetPerson, setSheetPerson] = useState(null);
  const [sheetLane, setSheetLane] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showBoard, setShowBoard] = useState(true);

  const destinations = useMemo(
    () => assignDestinations(lanes, sheetPerson?.id),
    [lanes, sheetPerson]
  );

  const reliefRows = useMemo(
    () => stagedReliefRows(assignments, { nowMin, resolveRoom: (o) => roomForOrRoom(o, rooms) }),
    [assignments, rooms, nowMin]
  );
  const reliefByRoomId = useMemo(() => {
    const m = new Map();
    for (const r of reliefRows) if (r.roomId) m.set(r.roomId, r);
    return m;
  }, [reliefRows]);

  const boundary = useMemo(
    () => nextBoundary(model.staffing, nowMin, model.doctorStaffing),
    [model.staffing, model.doctorStaffing, nowMin]
  );

  const uncoveredRooms = useMemo(
    () =>
      lanes
        .filter((l) => alarms.uncoveredRoomIds?.has(l.roomId))
        .map((l) => ({ roomId: l.roomId, roomName: l.roomName })),
    [lanes, alarms]
  );

  const worklist = useMemo(
    () =>
      buildWorklist({
        nowMin,
        emergencies: (alertRows || []).map((a) => ({
          roomId: a.roomId, roomName: a.roomName,
          acknowledgedBy: a.acknowledgedBy, ageMin: a.ageMin,
        })),
        uncoveredRooms,
        breaksWaiting: (breakWaitingRows || []).map((b) => ({
          roomName: b.roomName, providerName: b.providerName,
          ageMin: b.ageMin,
        })),
        reliefRows,
        lanes,
        bench: available?.providers || [],
        boundary,
        coverageOf: laneCoverage,
      }),
    [nowMin, alertRows, uncoveredRooms, breakWaitingRows, reliefRows, lanes, available, boundary]
  );

  const guard = useCallback(() => {
    const gate = writesAllowed({ canAct, connectionBand: connection.band });
    if (!gate.allowed) {
      setError(WRITE_BLOCKED_COPY[gate.reason] || "That is not allowed right now.");
      return false;
    }
    return true;
  }, [canAct, connection.band]);

  // Send the person in a room home for the day. Marking them departed is what
  // takes them out of the staffing maths at every later boundary, so it is a
  // real change and not a note.
  const onSendHome = useCallback(
    async (lane) => {
      if (!guard()) return;
      const who = currentCrnaId(lane);
      if (!who) { setError(`${lane.roomName} has nobody to send home.`); return; }
      setBusy(true);
      try {
        await setRosterMemberDeparted(facilityId, dateKey, who, true, user?.uid);
        setError(null);
      } catch (e) { setError(e?.message || "That person could not be sent home."); }
      finally { setBusy(false); }
    },
    [guard, facilityId, dateKey, user]
  );

  const onStatus = useCallback(
    async (lane, status) => {
      if (!guard()) return;
      setBusy(true);
      try { await updateRoomStatus(lane.roomId, status); setError(null); }
      catch (e) { setError(e?.message || "That status could not be saved."); }
      finally { setBusy(false); }
    },
    [guard]
  );

  const commitPlacement = useCallback(
    async ({ person, lane: destLane, displaced, disposition, travelDest, travelDestName }) => {
      if (!guard()) return;
      const liveLane = lanes.find((l) => l.roomId === destLane.roomId) || null;
      const liveCase = targetCaseFor(liveLane);
      const conflict = assignmentChangedSince({ ahpId: displaced ? displaced.id : null }, liveCase);
      if (conflict) { setError(conflict); return; }
      setBusy(true);
      try {
        await placeProvider({
          person: person !== undefined ? person : sheetPerson,
          lane: liveLane || destLane, targetCase: liveCase,
          displaced, disposition, travelDest, travelDestName,
          facilityId, dateKey, actorUid: user?.uid,
        });
        setSheetPerson(null); setSheetLane(null); setError(null);
      } catch (e) { setError(e?.message || "That change could not be saved."); }
      finally { setBusy(false); }
    },
    [guard, lanes, sheetPerson, facilityId, dateKey, user]
  );

  const onConfirmRelief = useCallback(
    async (row) => {
      if (!guard()) return;
      setBusy(true);
      try { await confirmRelief(row.assignment, { roomId: row.roomId }); setError(null); }
      catch (e) { setError(e?.message || "That relief could not be confirmed."); }
      finally { setBusy(false); }
    },
    [guard]
  );

  const sections = useMemo(
    () => groupRoomsByLocation(occupiedRooms || [], locations || []),
    [occupiedRooms, locations]
  );
  const laneByRoomId = useMemo(() => {
    const m = new Map();
    for (const l of lanes) m.set(l.roomId, l);
    return m;
  }, [lanes]);

  const label = facilityLabel(facilityId, facilities);

  if (!facilityId) {
    return (
      <div style={styles.page}>
        <FacilityPicker
          reason={device.reason} candidates={device.candidates}
          onChoose={device.choose} onRetry={device.retry}
          onSignOut={logout} busy={device.checking}
        />
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", background: C.card, borderBottom: `1px solid ${C.line}`, flexWrap: "wrap" }}>
        <NavMenu />
        <span style={{ fontWeight: 800, color: C.inkStrong }}>OR Board B</span>
        <BoardSwitcher compact />
        <span style={{ fontFamily: MONO, fontSize: 12, color: C.muted }}>{label} · {dateKey}</span>
        <span
          style={{ ...styles.flag, ...(connection?.level === "live" ? styles.flagGood : styles.flagAlarm) }}
          data-testid="b-connection"
          data-level={connection?.level}
        >
          <span aria-hidden="true">{connection?.glyph || "○"}</span>
          {connection?.rail || "CONNECTING…"}
        </span>
        {!canAct ? <span style={styles.flag}>READ ONLY</span> : null}
        {/* marginLeft:auto — see BoardAPage; this row wraps, so the control
            still lands last rather than being pinned to a corner. */}
        <span style={{ marginLeft: "auto" }}><SignOutButton /></span>
      </div>

      {connection.band ? (
        <div role="alert" style={{ background: C.alarmBg, borderBottom: `2px solid ${C.alarm}`, color: "#7f1d1d", fontWeight: 800, padding: "8px 16px" }}>
          ▲ {connection.band}
        </div>
      ) : null}

      {error ? (
        <div style={{ background: C.warnBg, borderBottom: `1px solid ${C.warnLine}`, color: C.warn, fontWeight: 700, padding: "8px 16px", display: "flex", gap: 12 }}>
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} style={{ ...styles.act, marginLeft: "auto", minHeight: 26 }}>Dismiss</button>
        </div>
      ) : null}

      {/* ── THE WORKLIST ────────────────────────────────────────────────── */}
      <section style={{ margin: "14px 16px" }}>
        <h2 style={{ ...styles.sectionHead, padding: "0 0 8px" }}>
          Needs you
          <span style={styles.countChip}>{worklist.length}</span>
        </h2>

        {worklist.length === 0 ? (
          <div style={{ background: C.card, border: `1px solid ${C.goodLine}`, borderRadius: C.radius, padding: 16 }}>
            <p style={{ margin: 0, fontWeight: 800, color: C.good, fontSize: 15 }}>
              ✓ Nothing needs you right now
            </p>
            <p style={{ margin: "8px 0 4px", fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase", color: C.mutedSoft }}>
              Quietly watching
            </p>
            <ul style={{ margin: 0, paddingLeft: 18, color: C.muted, fontSize: 12.5 }}>
              {watchingSummary({
                roomsRunning: railMetrics.roomsRunning,
                boundary,
                bench: railMetrics.bench,
              }).map((line) => <li key={line}>{line}</li>)}
            </ul>
          </div>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
            {worklist.map((item) => {
              const look = TIER_LOOK[item.tier] || TIER_LOOK.plan;
              return (
                <li
                  key={item.key}
                  data-tier={item.tier}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    background: C.card,
                    border: `1px solid ${look.line}`,
                    borderLeft: `5px solid ${look.fg}`,
                    borderRadius: C.radius,
                    padding: "10px 12px",
                  }}
                >
                  <span
                    style={{
                      minWidth: 68, textAlign: "center",
                      fontSize: 10.5, fontWeight: 800, letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      background: look.bg, color: look.fg,
                      border: `1px solid ${look.line}`,
                      borderRadius: 999, padding: "3px 6px",
                    }}
                  >
                    {TIER_LABEL[item.tier]}
                  </span>
                  <span aria-hidden="true" style={{ fontSize: 15, color: look.fg }}>{item.glyph}</span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", fontWeight: 700, fontSize: 13.5 }}>{item.text}</span>
                    {item.detail ? (
                      <span style={{ display: "block", fontSize: 12, color: C.muted }}>{item.detail}</span>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {sheetPerson || sheetLane ? (
        <div style={{ margin: "12px 16px", maxWidth: 520 }}>
          <PanelSheet
            person={sheetPerson} lane={sheetLane}
            bench={available?.providers || []}
            destinations={destinations} facilities={facilities}
            busy={busy} onCommit={commitPlacement}
            onClose={() => { setSheetPerson(null); setSheetLane(null); }}
          />
        </div>
      ) : null}

      {/* The board stays available and secondary. Collapsible, because on a busy
          day the worklist is long and the rooms are a scroll away. */}
      <div style={{ padding: "0 16px" }}>
        <button type="button" onClick={() => setShowBoard((v) => !v)} style={styles.act} aria-expanded={showBoard}>
          {showBoard ? "▾ Hide the board" : "▸ Show the board"}
        </button>
      </div>

      {showBoard ? (
        <>
          {loading ? (
            <p style={{ padding: "24px 16px", color: C.muted }}>Loading the day…</p>
          ) : (
            sections.map((sec) => {
              if (!sec.rooms.length) return null;
              return (
                <section key={sec.key} style={{ ...styles.section, background: sectionTint(sec.label) }}>
                  <h2 style={styles.sectionHead}>
                    {sec.label}
                    <span style={styles.countChip}>{sec.rooms.length}</span>
                  </h2>
                  <div style={styles.grid}>
                    {sec.rooms.map((room) => {
                      const lane = laneByRoomId.get(room.id) || {
                        roomId: room.id, roomName: room.name, sectionLabel: sec.label, cases: [],
                      };
                      return (
                        <PanelRoomCard
                          key={room.id} lane={lane} room={room}
                          axisStart={model.axisStart} axisEnd={model.axisEnd}
                          nowMin={nowMin} alarms={alarms}
                          breakRow={breakRowByRoomId.get(room.id)}
                          breakWindows={breakWindows}
                          reliefRow={reliefByRoomId.get(room.id)}
                          canAct={canAct}
                          onAssign={() =>
                            setSheetLane({
                              roomId: lane.roomId, roomName: lane.roomName,
                              holderId: currentCrnaId(lane), holderName: currentCrnaName(lane),
                            })
                          }
                          onEditTimes={() => setError("Times editing lives on OR Board C for now.")}
                          onRelieve={(l) => setError(`Staging relief for ${l.roomName} is next; Confirm already works when one is staged.`)}
                          onSendHome={(l) => onSendHome(l)}
                          onMessage={() => window.dispatchEvent(new CustomEvent("atc-open-messages"))}
                          onStatus={(l, st) => onStatus(l, st)}
                          onMoveRoom={(l) =>
                            setSheetLane({
                              roomId: l.roomId, roomName: l.roomName,
                              holderId: currentCrnaId(l), holderName: currentCrnaName(l),
                            })
                          }
                          onConfirmRelief={onConfirmRelief}
                          onCoverBreak={() => setError("Break lives on OR Board C for now.")}
                        />
                      );
                    })}
                  </div>
                </section>
              );
            })
          )}

          <BenchSections
            bench={available?.providers || []}
            doctors={available?.doctors || []}
            departed={departedMembers}
            traveling={travelingMembers}
            canAct={canAct}
            onAssign={(person) => setSheetPerson(person)}
          />
        </>
      ) : null}
    </div>
  );
}
